import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ArrowLeft, Globe, RotateCcw, Info, Plus, Minus, 
  Atom, Zap, Circle, ChevronRight, ChevronLeft, Volume2
} from 'lucide-react';

/**
 * THE ELEMENT BUILDER - Interactive Periodic Table Simulation
 * Students drag protons, neutrons, electrons to build atoms
 * Watch the element change on periodic table in real-time
 */
const ElementBuilderSimulation = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [language, setLanguage] = useState('en');
  const [showInfo, setShowInfo] = useState(false);
  const [protons, setProtons] = useState(1);
  const [neutrons, setNeutrons] = useState(0);
  const [electrons, setElectrons] = useState(1);
  const [animationFrame, setAnimationFrame] = useState(0);
  const [showElectronShells, setShowElectronShells] = useState(true);

  // Periodic table data
  const elements = [
    { symbol: 'H', name: 'Hydrogen', nameHi: 'हाइड्रोजन', z: 1, mass: 1.008 },
    { symbol: 'He', name: 'Helium', nameHi: 'हीलियम', z: 2, mass: 4.003 },
    { symbol: 'Li', name: 'Lithium', nameHi: 'लिथियम', z: 3, mass: 6.941 },
    { symbol: 'Be', name: 'Beryllium', nameHi: 'बेरिलियम', z: 4, mass: 9.012 },
    { symbol: 'B', name: 'Boron', nameHi: 'बोरॉन', z: 5, mass: 10.81 },
    { symbol: 'C', name: 'Carbon', nameHi: 'कार्बन', z: 6, mass: 12.01 },
    { symbol: 'N', name: 'Nitrogen', nameHi: 'नाइट्रोजन', z: 7, mass: 14.01 },
    { symbol: 'O', name: 'Oxygen', nameHi: 'ऑक्सीजन', z: 8, mass: 16.00 },
    { symbol: 'F', name: 'Fluorine', nameHi: 'फ्लोरीन', z: 9, mass: 19.00 },
    { symbol: 'Ne', name: 'Neon', nameHi: 'नियॉन', z: 10, mass: 20.18 },
    { symbol: 'Na', name: 'Sodium', nameHi: 'सोडियम', z: 11, mass: 22.99 },
    { symbol: 'Mg', name: 'Magnesium', nameHi: 'मैग्नीशियम', z: 12, mass: 24.31 },
    { symbol: 'Al', name: 'Aluminium', nameHi: 'एल्युमिनियम', z: 13, mass: 26.98 },
    { symbol: 'Si', name: 'Silicon', nameHi: 'सिलिकॉन', z: 14, mass: 28.09 },
    { symbol: 'P', name: 'Phosphorus', nameHi: 'फास्फोरस', z: 15, mass: 30.97 },
    { symbol: 'S', name: 'Sulfur', nameHi: 'सल्फर', z: 16, mass: 32.07 },
    { symbol: 'Cl', name: 'Chlorine', nameHi: 'क्लोरीन', z: 17, mass: 35.45 },
    { symbol: 'Ar', name: 'Argon', nameHi: 'आर्गन', z: 18, mass: 39.95 },
    { symbol: 'K', name: 'Potassium', nameHi: 'पोटैशियम', z: 19, mass: 39.10 },
    { symbol: 'Ca', name: 'Calcium', nameHi: 'कैल्शियम', z: 20, mass: 40.08 },
  ];

  const currentElement = elements.find(e => e.z === protons) || { symbol: '?', name: 'Unknown', nameHi: 'अज्ञात', z: protons, mass: protons + neutrons };

  // Calculate electron configuration
  const getElectronShells = (numElectrons) => {
    const shells = [0, 0, 0, 0]; // K, L, M, N shells
    let remaining = numElectrons;
    const maxPerShell = [2, 8, 18, 32];
    
    for (let i = 0; i < shells.length && remaining > 0; i++) {
      shells[i] = Math.min(remaining, maxPerShell[i]);
      remaining -= shells[i];
    }
    return shells;
  };

  const electronShells = getElectronShells(electrons);

  // Determine ion charge
  const charge = protons - electrons;
  const chargeText = charge === 0 ? '' : charge > 0 ? `${charge}+` : `${Math.abs(charge)}-`;

  // Translations
  const t = {
    title: language === 'en' ? 'The Element Builder' : 'तत्व निर्माता',
    subtitle: language === 'en' ? 'Build atoms by adding subatomic particles' : 'उप-परमाणु कणों को जोड़कर परमाणु बनाएं',
    protons: language === 'en' ? 'Protons' : 'प्रोटॉन',
    neutrons: language === 'en' ? 'Neutrons' : 'न्यूट्रॉन',
    electrons: language === 'en' ? 'Electrons' : 'इलेक्ट्रॉन',
    nucleus: language === 'en' ? 'Nucleus' : 'नाभिक',
    atomicNumber: language === 'en' ? 'Atomic Number' : 'परमाणु क्रमांक',
    massNumber: language === 'en' ? 'Mass Number' : 'द्रव्यमान संख्या',
    charge: language === 'en' ? 'Charge' : 'आवेश',
    neutral: language === 'en' ? 'Neutral' : 'उदासीन',
    cation: language === 'en' ? 'Cation (+)' : 'धनायन (+)',
    anion: language === 'en' ? 'Anion (-)' : 'ऋणायन (-)',
    electronConfig: language === 'en' ? 'Electron Configuration' : 'इलेक्ट्रॉन विन्यास',
    shell: language === 'en' ? 'Shell' : 'कोश',
    info: language === 'en' ? 'How it works' : 'यह कैसे काम करता है',
    infoText: language === 'en' 
      ? 'Add protons to change the element. Protons determine which element it is! Neutrons affect the mass (isotopes). Electrons determine the charge (ions). Watch the periodic table highlight your element!'
      : 'तत्व बदलने के लिए प्रोटॉन जोड़ें। प्रोटॉन निर्धारित करते हैं कि यह कौन सा तत्व है! न्यूट्रॉन द्रव्यमान को प्रभावित करते हैं (समस्थानिक)। इलेक्ट्रॉन आवेश निर्धारित करते हैं (आयन)। देखें आवर्त सारणी में आपका तत्व हाइलाइट होता है!',
    reset: language === 'en' ? 'Reset to Hydrogen' : 'हाइड्रोजन पर रीसेट करें',
    quickElements: language === 'en' ? 'Quick Elements' : 'त्वरित तत्व',
  };

  // Animation loop for electrons
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationFrame(f => f + 1);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Draw atom
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    
    // Clear
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, width, height);
    
    // Draw electron shells (orbits)
    if (showElectronShells) {
      const shellRadii = [60, 100, 140, 180];
      electronShells.forEach((count, shellIdx) => {
        if (count > 0) {
          ctx.strokeStyle = `rgba(59, 130, 246, ${0.3 + shellIdx * 0.1})`;
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);
          ctx.beginPath();
          ctx.arc(centerX, centerY, shellRadii[shellIdx], 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          
          // Draw electrons on this shell
          for (let e = 0; e < count; e++) {
            const angle = (animationFrame * 0.02 * (4 - shellIdx)) + (e * Math.PI * 2 / count);
            const ex = centerX + Math.cos(angle) * shellRadii[shellIdx];
            const ey = centerY + Math.sin(angle) * shellRadii[shellIdx];
            
            // Electron glow
            const gradient = ctx.createRadialGradient(ex, ey, 0, ex, ey, 12);
            gradient.addColorStop(0, '#60a5fa');
            gradient.addColorStop(1, 'transparent');
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(ex, ey, 12, 0, Math.PI * 2);
            ctx.fill();
            
            // Electron core
            ctx.fillStyle = '#3b82f6';
            ctx.beginPath();
            ctx.arc(ex, ey, 6, 0, Math.PI * 2);
            ctx.fill();
            
            // Electron label
            ctx.fillStyle = 'white';
            ctx.font = 'bold 8px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('e⁻', ex, ey);
          }
        }
      });
    }
    
    // Draw nucleus
    const nucleusRadius = 20 + Math.min(protons + neutrons, 40) * 0.5;
    
    // Nucleus glow
    const nucleusGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, nucleusRadius + 20);
    nucleusGradient.addColorStop(0, 'rgba(239, 68, 68, 0.3)');
    nucleusGradient.addColorStop(1, 'transparent');
    ctx.fillStyle = nucleusGradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, nucleusRadius + 20, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw protons and neutrons in nucleus
    const particles = [];
    for (let i = 0; i < protons; i++) particles.push({ type: 'proton' });
    for (let i = 0; i < neutrons; i++) particles.push({ type: 'neutron' });
    
    particles.forEach((p, i) => {
      const angle = (i / particles.length) * Math.PI * 2 + animationFrame * 0.01;
      const r = Math.min(i * 3, nucleusRadius - 10) * (0.3 + Math.random() * 0.1);
      const px = centerX + Math.cos(angle + i) * r;
      const py = centerY + Math.sin(angle + i) * r;
      
      ctx.fillStyle = p.type === 'proton' ? '#ef4444' : '#6b7280';
      ctx.beginPath();
      ctx.arc(px, py, 8, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = 'white';
      ctx.font = 'bold 8px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(p.type === 'proton' ? 'p⁺' : 'n', px, py);
    });
    
    // Element symbol in center
    ctx.fillStyle = 'white';
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(currentElement.symbol, centerX, centerY);
    
  }, [protons, neutrons, electrons, animationFrame, showElectronShells, currentElement.symbol]);

  // Reset to hydrogen
  const resetAtom = () => {
    setProtons(1);
    setNeutrons(0);
    setElectrons(1);
  };

  // Quick element selection
  const quickElements = [
    { z: 1, symbol: 'H' },
    { z: 6, symbol: 'C' },
    { z: 8, symbol: 'O' },
    { z: 11, symbol: 'Na' },
    { z: 17, symbol: 'Cl' },
    { z: 20, symbol: 'Ca' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-900/90 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/science')} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
              <Atom className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{t.title}</h1>
              <p className="text-xs text-slate-400">{t.subtitle}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button onClick={() => setShowInfo(!showInfo)} className={`p-2 rounded-lg transition-colors ${showInfo ? 'bg-purple-600' : 'bg-slate-800 hover:bg-slate-700'}`}>
              <Info className="w-5 h-5 text-white" />
            </button>
            <button onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
              className="flex items-center gap-1 px-3 py-2 bg-slate-800 rounded-full hover:bg-slate-700 transition-colors">
              <Globe className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-slate-300">{language === 'en' ? 'हिन्दी' : 'ENG'}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        {showInfo && (
          <div className="mb-4 p-4 bg-purple-500/20 border border-purple-500/30 rounded-xl">
            <p className="text-sm text-purple-200">{t.infoText}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Controls */}
          <div className="space-y-4">
            {/* Particle Controls */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 space-y-4">
                {/* Protons */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-red-400 flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      {t.protons}
                    </span>
                    <span className="text-2xl font-bold text-white">{protons}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => setProtons(Math.max(1, protons - 1))} size="sm" variant="outline" className="border-slate-600">
                      <Minus className="w-4 h-4" />
                    </Button>
                    <input type="range" min="1" max="20" value={protons} onChange={(e) => setProtons(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-red-500" />
                    <Button onClick={() => setProtons(Math.min(20, protons + 1))} size="sm" variant="outline" className="border-slate-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Neutrons */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-400 flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-500 rounded-full"></div>
                      {t.neutrons}
                    </span>
                    <span className="text-2xl font-bold text-white">{neutrons}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => setNeutrons(Math.max(0, neutrons - 1))} size="sm" variant="outline" className="border-slate-600">
                      <Minus className="w-4 h-4" />
                    </Button>
                    <input type="range" min="0" max="30" value={neutrons} onChange={(e) => setNeutrons(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-gray-500" />
                    <Button onClick={() => setNeutrons(Math.min(30, neutrons + 1))} size="sm" variant="outline" className="border-slate-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Electrons */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-400 flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                      {t.electrons}
                    </span>
                    <span className="text-2xl font-bold text-white">{electrons}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => setElectrons(Math.max(0, electrons - 1))} size="sm" variant="outline" className="border-slate-600">
                      <Minus className="w-4 h-4" />
                    </Button>
                    <input type="range" min="0" max="20" value={electrons} onChange={(e) => setElectrons(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500" />
                    <Button onClick={() => setElectrons(Math.min(20, electrons + 1))} size="sm" variant="outline" className="border-slate-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <Button onClick={resetAtom} variant="outline" className="w-full border-slate-600 text-slate-300">
                  <RotateCcw className="w-4 h-4 mr-2" /> {t.reset}
                </Button>
              </CardContent>
            </Card>

            {/* Quick Elements */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-slate-300 mb-3">{t.quickElements}</h3>
                <div className="grid grid-cols-3 gap-2">
                  {quickElements.map(el => (
                    <button key={el.z} onClick={() => { setProtons(el.z); setNeutrons(el.z); setElectrons(el.z); }}
                      className={`p-3 rounded-lg border transition-all text-center ${
                        protons === el.z ? 'border-purple-500 bg-purple-500/20' : 'border-slate-600 bg-slate-700/50 hover:bg-slate-700'
                      }`}>
                      <div className="text-xl font-bold text-white">{el.symbol}</div>
                      <div className="text-xs text-slate-400">{el.z}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Atom Visualization */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <canvas ref={canvasRef} width={500} height={400} className="w-full rounded-xl" />
                
                {/* Element Info */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <div className="text-4xl font-bold text-white mb-1">{currentElement.symbol}</div>
                    <div className="text-sm text-slate-400">{language === 'en' ? currentElement.name : currentElement.nameHi}</div>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-red-400 mb-1">{protons}</div>
                    <div className="text-xs text-slate-400">{t.atomicNumber} (Z)</div>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gray-400 mb-1">{protons + neutrons}</div>
                    <div className="text-xs text-slate-400">{t.massNumber} (A)</div>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <div className={`text-2xl font-bold mb-1 ${charge === 0 ? 'text-green-400' : charge > 0 ? 'text-red-400' : 'text-blue-400'}`}>
                      {chargeText || '0'}
                    </div>
                    <div className="text-xs text-slate-400">
                      {charge === 0 ? t.neutral : charge > 0 ? t.cation : t.anion}
                    </div>
                  </div>
                </div>

                {/* Electron Configuration */}
                <div className="mt-4 bg-slate-700/50 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-slate-300 mb-3">{t.electronConfig}</h4>
                  <div className="flex items-center justify-center gap-4">
                    {['K', 'L', 'M', 'N'].map((shell, idx) => (
                      electronShells[idx] > 0 && (
                        <div key={shell} className="text-center">
                          <div className="w-12 h-12 rounded-full border-2 border-blue-400 flex items-center justify-center bg-blue-500/20">
                            <span className="text-lg font-bold text-blue-400">{electronShells[idx]}</span>
                          </div>
                          <div className="text-xs text-slate-400 mt-1">{shell} {t.shell}</div>
                        </div>
                      )
                    ))}
                  </div>
                  <div className="text-center mt-3 text-slate-400 text-sm font-mono">
                    {electronShells.filter(s => s > 0).join(', ')}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElementBuilderSimulation;
